import React, { Suspense, useMemo, useCallback } from "react";
import clsx from "clsx";

import { useResource, useFetcher } from "@rest-hooks/core";
import { makeStyles } from "@material-ui/core/styles";
import CarOrderActions from "./CarOrderActions";
import { CarOrderDescription } from "./CarOrderDescription";
import { LinearProgress, TablePagination } from "@material-ui/core";
import {
	ExpandableTable,
	ExpandedRow,
	Cells,
	ExpandableTableProps
} from "@ploy-ui/core";
import { ExpandedRowProps } from "@ploy-ui/core";
import {
	CarOrderDetails,
	ApplicationInfo,
	CarOrderDetailsResource,
	ApplicationInfoResource,
	ApplicationInfoFilter
} from "@ploy-lib/rest-resources";
import { ApplicationStatus, DocumentStatus } from "@ploy-lib/types";
import { FormattedDate } from "react-intl";

const useStyles = makeStyles(
	theme => ({
		root: {
			width: "100%",
			flexGrow: 1,
			padding: theme.spacing(2),
			[theme.breakpoints.up("lg")]: {
				padding: theme.spacing(4)
			}
		},
		applicationStatusNameExternal: {
			width: 160,
			textAlign: "center" as "center"
		},
		applicationNumber: {
			width: 100
		},
		customerNameInner: {
			display: "flex",
			alignItems: "center" as "center",
			"& > div": {
				marginRight: 10
			}
		},
		customerNameText: {
			fontWeight: "bold" as "bold"
		}
	}),
	{ name: "PendingOrders" }
);

const defaultColumnData: ExpandableTableProps<
	ApplicationInfo,
	CarOrderDetails
>["columnData"] = [
	{
		id: "applicationStatusNameExternal",
		label: "Status",
		component: props => (
			<Cells.ColoredStatus statusKey="applicationStatusId" {...props} />
		),
		hidden: { xsDown: true },
		hiddenInExpandable: { smUp: true }
	},
	{
		id: "documentStatusNameExternal",
		label: "Dokumentstatus"
	},
	{
		id: "applicationNumber",
		label: "Saksnr.",
		hidden: { smDown: true }
	},
	{
		id: "customerName",
		label: "Kunde",
		component: props => (
			<Cells.DoubleLineSmDown
				statusKey="applicationStatusId"
				secondLineKey="applicationNumber"
				{...props}
			/>
		)
	},
	{
		id: "field5",
		label: "Merke"
	},
	{
		id: "field7",
		label: "Model"
	},
	{
		id: "field8",
		label: "Regnr."
	},
	{
		id: "productName",
		label: "Produkt",
		hidden: { smDown: true },
		hiddenInExpandable: { mdUp: true }
	},
	{
		id: "salesPersonName",
		label: "Selger",
		hidden: true
	},
	{
		loadedId: "car.deliveryLocation",
		label: "Leveringssted"
	},
	{
		loadedId: "customer.fullName",
		label: "Kunde"
	},
	{
		loadedId: "car.interior",
		label: "Interiør"
	},
	{
		loadedId: "car.color",
		label: "Farge"
	},
	{
		loadedId: "car.description",
		label: "Bilbeskrivelse"
	},
	{
		loadedId: "selectedDriver.fullName",
		label: "Fører",
		hiddenInExpandable: val => val === undefined || val === null
	},
	{
		loadedId: "deliveryDate",
		label: "Forventet leveringsdato",
		hiddenInExpandable: val => val === undefined || val === null,
		format: val =>
			val && (
				<FormattedDate value={val} day="numeric" month="long" year="numeric" />
			)
	},
	{
		component: CarOrderDescription
	},
	{
		component: CarOrderActions
	}
];

export interface PendingOrdersProps {
	columnData?: ExpandableTableProps<
		ApplicationInfo,
		CarOrderDetails
	>["columnData"];
	className?: string;
}

const pendingOrdersFilter: ApplicationInfoFilter = {
	appStatus: [
		ApplicationStatus.AutoApproved,
		ApplicationStatus.ManuallyApproved
	],
	docStatus: [
		DocumentStatus.CarOrderReceived,
		DocumentStatus.CarOrderConfirmationReceived,
		DocumentStatus.DocumentsProduced,
		DocumentStatus.ESigned,
		DocumentStatus.DocumentsReceived,
		DocumentStatus.DocumentControl,
		DocumentStatus.DocumentsOK,
		DocumentStatus.NewDocumentsReceived,
		DocumentStatus.ProofOfDeliverySent,
		DocumentStatus.ProofOfDeliveryAccepted
	],
	productExceptions: [203],
	productList: [203]
};

export const PendingOrders = ({
	className,
	columnData = defaultColumnData
}: PendingOrdersProps) => {
	const classes = useStyles();

	const columns = useMemo(
		() =>
			columnData.map(c => ({
				...c,
				className:
					c.id == null
						? undefined
						: (classes as Record<string, string | undefined>)[c.id]
			})),
		[columnData, classes]
	);

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(20);

	const params = useMemo<ApplicationInfoFilter>(
		() => ({
			...pendingOrdersFilter,
			pageSize: rowsPerPage,
			page: page + 1,
			sortBy: "createdDate.desc",
			/* add caradm product id. Needed for displaying apps related to internal products. Move to dt (See km reading module  for example) if other customers are going to use this module */
			productExceptions: [203],
			mustHaveHadDocStatus: 83
		}),
		[page, rowsPerPage]
	);

	const { records, pageSize, recordCount } = useResource(
		ApplicationInfoResource.list(),
		params
	);

	const fetchApplications = useFetcher(ApplicationInfoResource.list());

	const reload = useCallback(
		() => fetchApplications(params),
		[fetchApplications, params]
	);

	return (
		<div className={clsx(className, classes.root)}>
			<ExpandableTable
				idColumn="applicationId"
				tableData={records as ApplicationInfo[]}
				columnData={columns}
				renderExpandedSection={row => {
					return (
						<Suspense fallback={<LinearProgress />}>
							<CarOrderExpandedRow
								columnData={columns}
								rowData={row}
								reload={reload}
							/>
						</Suspense>
					);
				}}
			/>
			<TablePagination
				rowsPerPageOptions={[10, 20, 50]}
				component="div"
				count={recordCount}
				rowsPerPage={pageSize}
				page={page}
				onPageChange={(event, newPage) => {
					setPage(newPage);
				}}
				onRowsPerPageChange={event => {
					setRowsPerPage(parseInt(event.target.value, 10));
					setPage(0);
				}}
			/>
		</div>
	);
};

const CarOrderExpandedRow = (
	props: ExpandedRowProps<ApplicationInfo, CarOrderDetails> & {
		reload: () => Promise<any>;
	}
) => {
	const { rowData } = props;

	const carOrderDetails = useResource(CarOrderDetailsResource.detail(), {
		applicationId: rowData.applicationId
	});

	const reloadCarOrderDetails = useFetcher(CarOrderDetailsResource.detail());

	return (
		<ExpandedRow
			loadedData={carOrderDetails}
			reloadData={reloadCarOrderDetails}
			{...props}
		/>
	);
};
